import { paraElServidor as aDecimal, decimalOpcional, normalizarDecimal } from "@/lib/decimales"

/**
 * Comprobantes de retención provisional, tal como los devuelve la API.
 *
 * ⚠ **Los montos viajan como TEXTO decimal** (`"1041287.15"`), porque son `Decimal`
 * en el servidor (regla 18). Aquí no se suman ni se redondean: los totales los
 * calcula el servidor (`POST /retenciones-provisionales/calcular`) y la pantalla los
 * pinta con `monto()`.
 */

export type LineaRetencion = {
  descripcion: string
  /** Base del servicio en bolívares, sin IVA, a la tasa de EMISIÓN. */
  monto: string
  /**
   * Lo que dice el documento en dólares, si lo trae. Con la tasa de emisión, los
   * bolívares los pone el servidor y `monto` se ignora; es lo que permite reajustar a
   * otra tasa sin arrastrar el redondeo.
   */
  monto_usd: string
  exento_iva: boolean
  retiene_iva: boolean
  retiene_islr: boolean
}

export type Calculo = {
  base_gravada: string
  base_exenta: string
  subtotal: string
  monto_iva: string
  base_iva_sujeta: string
  iva_sujeto: string
  monto_ret_iva: string
  base_islr: string
  monto_ret_islr: string
  total: string
  total_retenido: string
  neto: string
}

/** El documento recalculado a la tasa del pago, y cuánto cambió contra la emisión. */
export type Reajuste = {
  tasa_emision: string
  tasa_reajuste: string
  montos: string[]
  calculo: Calculo
  diferencia_total: string
  diferencia_retenido: string
  diferencia_neto: string
}

/**
 * Lo que contesta `/calcular`: los totales a la tasa de EMISIÓN —los de `Calculo`— y,
 * si hay tasa de reajuste, el recálculo. Lo que se imprime es `vigente()`.
 */
export type Evaluacion = Calculo & {
  /** Base en bolívares de cada línea enviada, en el mismo orden. */
  montos: string[]
  reajuste: Reajuste | null
}

/** Lo que se imprime y se paga: el reajustado si lo hay. */
export function vigente(e: Evaluacion): { calculo: Calculo; montos: string[] } {
  return e.reajuste
    ? { calculo: e.reajuste.calculo, montos: e.reajuste.montos }
    : { calculo: e, montos: e.montos }
}

export type Porcentajes = {
  alicuota_iva: string
  porcentaje_ret_iva: string
  porcentaje_ret_islr: string
  sustraendo_islr: string
}

/** Lo que se escribe en el formulario y se manda tal cual al guardar. */
export type BorradorRetencion = Porcentajes & {
  fecha: string
  agente_nombre: string
  agente_rif: string
  cliente_id: number | null
  beneficiario_nombre: string
  beneficiario_rif: string
  proveedor_id: number | null
  documento: string
  bl: string
  concepto: string
  /** La de EMISIÓN del documento. */
  tasa_cambio: string
  /** La del pago, si es otra: el comprobante se recalcula entero con ella. */
  tasa_reajuste: string
  fecha_reajuste: string
  firmante: string
  observaciones: string
  lineas: LineaRetencion[]
}

export type Retencion = Omit<
  BorradorRetencion,
  | "agente_rif"
  | "beneficiario_rif"
  | "documento"
  | "bl"
  | "concepto"
  | "tasa_cambio"
  | "tasa_reajuste"
  | "fecha_reajuste"
  | "lineas"
  | "firmante"
  | "observaciones"
> & {
  id: number
  codigo: string
  agente_rif: string | null
  beneficiario_rif: string | null
  documento: string | null
  bl: string | null
  concepto: string | null
  tasa_cambio: string | null
  tasa_reajuste: string | null
  fecha_reajuste: string | null
  firmante: string | null
  observaciones: string | null
  creado_en: string
  actualizado_en: string
  lineas: (Omit<LineaRetencion, "monto_usd"> & { monto_usd: string | null })[]
  calculo: Evaluacion
}

export type ResumenRetencion = {
  id: number
  codigo: string
  fecha: string
  agente_nombre: string
  beneficiario_nombre: string
  documento: string | null
  bl: string | null
  /** Los vigentes: a la tasa de reajuste si la hay. */
  total_retenido: string
  neto: string
  reajustado: boolean
}

export type ValoresPorOmision = {
  alicuota_iva: string
  porcentaje_ret_iva: string
  porcentaje_ret_islr: string
  firmante: string | null
}

export const lineaEnBlanco = (): LineaRetencion => ({
  descripcion: "",
  monto: "",
  monto_usd: "",
  exento_iva: false,
  retiene_iva: true,
  retiene_islr: true,
})

export function borradorEnBlanco(valores: ValoresPorOmision | undefined, hoy: string) {
  return {
    fecha: hoy,
    agente_nombre: "",
    agente_rif: "",
    cliente_id: null,
    beneficiario_nombre: "",
    beneficiario_rif: "",
    proveedor_id: null,
    documento: "",
    bl: "",
    concepto: "",
    tasa_cambio: "",
    tasa_reajuste: "",
    fecha_reajuste: "",
    firmante: valores?.firmante ?? "",
    observaciones: "",
    alicuota_iva: valores?.alicuota_iva ?? "16",
    porcentaje_ret_iva: valores?.porcentaje_ret_iva ?? "75",
    porcentaje_ret_islr: valores?.porcentaje_ret_islr ?? "2",
    sustraendo_islr: "0",
    lineas: [lineaEnBlanco()],
  } satisfies BorradorRetencion
}

/** Un comprobante guardado, de vuelta al formulario: los nulos pasan a texto vacío. */
export function comoBorrador(r: Retencion): BorradorRetencion {
  return {
    ...r,
    agente_rif: r.agente_rif ?? "",
    beneficiario_rif: r.beneficiario_rif ?? "",
    documento: r.documento ?? "",
    bl: r.bl ?? "",
    concepto: r.concepto ?? "",
    tasa_cambio: r.tasa_cambio ?? "",
    tasa_reajuste: r.tasa_reajuste ?? "",
    fecha_reajuste: r.fecha_reajuste ?? "",
    firmante: r.firmante ?? "",
    observaciones: r.observaciones ?? "",
    lineas: r.lineas.map(
      ({ descripcion, monto, monto_usd, exento_iva, retiene_iva, retiene_islr }) => ({
        descripcion,
        monto,
        monto_usd: monto_usd ?? "",
        exento_iva,
        retiene_iva,
        retiene_islr,
      }),
    ),
  }
}

/**
 * Lo que se manda al servidor: texto vacío → `null`, y sólo las líneas con monto.
 *
 * ⚠ **Una línea a medio escribir no se manda**, ni para calcular ni para guardar: un
 * renglón con descripción y sin monto todavía no dice nada, y mandarlo haría que la
 * previa fallara entera por un 422 mientras alguien teclea.
 */
export function paraElServidor(b: BorradorRetencion) {
  const nulo = (t: string) => (t.trim() === "" ? null : t.trim())
  return {
    ...b,
    agente_rif: nulo(b.agente_rif),
    beneficiario_rif: nulo(b.beneficiario_rif),
    documento: nulo(b.documento),
    bl: nulo(b.bl),
    concepto: nulo(b.concepto),
    tasa_cambio: decimalOpcional(nulo(b.tasa_cambio)),
    tasa_reajuste: decimalOpcional(nulo(b.tasa_reajuste)),
    fecha_reajuste: nulo(b.fecha_reajuste),
    alicuota_iva: aDecimal(b.alicuota_iva),
    porcentaje_ret_iva: aDecimal(b.porcentaje_ret_iva),
    porcentaje_ret_islr: aDecimal(b.porcentaje_ret_islr),
    firmante: nulo(b.firmante),
    observaciones: nulo(b.observaciones),
    sustraendo_islr: aDecimal(b.sustraendo_islr || "0"),
    lineas: lineasValidas(b.lineas),
  }
}

/** Una línea como viaja al servidor: el monto que falte va como `null`. */
export type LineaParaServidor = Omit<LineaRetencion, "monto" | "monto_usd"> & {
  monto: string | null
  monto_usd: string | null
}

/** Las líneas con algún monto —en bolívares o en dólares—, en su orden. */
export function lineasValidas(lineas: LineaRetencion[]): LineaParaServidor[] {
  return lineas.flatMap((l) => {
    const monto = normalizarDecimal(l.monto)
    const monto_usd = normalizarDecimal(l.monto_usd)
    if (monto === null && monto_usd === null) return []
    return [{ ...l, monto, monto_usd, descripcion: l.descripcion.trim() || "Servicio" }]
  })
}

/**
 * Para cada línea del formulario, su posición entre las que se mandan; `null` si no
 * se manda. Así se encuentra su monto en la respuesta, que sólo trae las enviadas.
 */
export function posicionesEnviadas(lineas: LineaRetencion[]): (number | null)[] {
  let n = 0
  return lineas.map((l) =>
    normalizarDecimal(l.monto) === null && normalizarDecimal(l.monto_usd) === null ? null : n++,
  )
}
